import { useState } from "react";
import CompletionTrend from "./CompletionTrend";
import {
  useAnimatedNumber,
  useInViewOnce,
} from "../../lib/parent/useAnimateOnVisible";

const headerColors = [
  "bg-gradient-to-r from-blue-700 to-blue-500 border-blue-600",
  "bg-gradient-to-r from-green-700 to-green-500 border-green-600",
  "bg-gradient-to-r from-amber-700 to-amber-500 border-amber-600",
  "bg-gradient-to-r from-violet-700 to-violet-500 border-violet-600",
];

function LessonStatusBadge({ completionPercent, index, isCurrent }) {
  if (completionPercent === 100) {
    return (
      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
        ✓
      </span>
    );
  }
  if (isCurrent) {
    return (
      <span className="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
        {index + 1}
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded-full bg-white border border-black/10 text-darkrock/40 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
      {index + 1}
    </span>
  );
}

function lessonBarColor(percent) {
  if (percent === 100) return "bg-emerald-500";
  if (percent > 0) return "bg-primary-600";
  return "bg-gray-200";
}

function TopicStatusBadge({ percent }) {
  if (percent === 100) {
    return (
      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
        ✓
      </span>
    );
  }
  if (percent > 0) {
    return (
      <span className="w-5 h-5 rounded-full bg-primary-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
        ▶
      </span>
    );
  }
  return (
    <span className="w-5 h-5 rounded-full bg-white border border-black/15 text-darkrock/40 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
      ○
    </span>
  );
}

function topicStatusLabel(percent) {
  if (percent === 100) return { label: "Completed", cls: "text-emerald-600" };
  if (percent > 0) return { label: "In progress", cls: "text-primary-600" };
  return { label: "Not started", cls: "text-gray-400" };
}

function ProgressRing({ percent, size = 64 }) {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const [ref, inView] = useInViewOnce();
  const animatedPercent = useAnimatedNumber(percent, { enabled: inView });
  const offset = circumference - (animatedPercent / 100) * circumference;
  const displayPercent = Math.round(animatedPercent);

  let strokeColor = "#3b82f6"; // blue-500
  if (percent === 100)
    strokeColor = "#22c55e"; // green-500
  else if (percent >= 75) strokeColor = "#06b6d4"; // cyan-500

  return (
    <svg ref={ref} width={size} height={size} className="flex-shrink-0">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="700"
        fill="#ffffff"
      >
        {displayPercent}%
      </text>
    </svg>
  );
}

function LessonRow({ lesson, index, isCurrent }) {
  const [expanded, setExpanded] = useState(false);
  const lp = lesson.completion_percent || 0;
  const topics = lesson.topics || [];
  const isDone = lp === 100;

  const rowBg = isCurrent
    ? "bg-pastelblue-light ring-1 ring-primary-600/30"
    : "hover:bg-gray-50";

  return (
    <div className="rounded-lg">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={`w-full flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1.5 ${rowBg}`}
      >
        <LessonStatusBadge completionPercent={lp} index={index} isCurrent={isCurrent} />
        <span
          className={`flex-1 text-sm truncate text-left ${
            isDone
              ? "text-darkrock/50 line-through"
              : isCurrent
              ? "text-darkrock font-semibold"
              : "text-gray-700"
          }`}
        >
          {lesson.title}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isCurrent && (
            <span className="px-2 py-0.5 rounded-full bg-primary-600 text-white text-[9px] font-bold tracking-wide">
              CURRENT
            </span>
          )}
          {lesson.avg_chat_score != null && lesson.avg_chat_score > 0 && (
            <span className="text-xs text-gray-500">
              ⭐ {lesson.avg_chat_score.toFixed(1)}
            </span>
          )}
          {!isDone && (
            <>
              <div className="w-24 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${lessonBarColor(lp)}`}
                  style={{ width: `${lp}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-500 w-8 text-right">
                {lp}%
              </span>
            </>
          )}
        </div>
      </button>
      {expanded && (
        <ul className="ml-9 mt-1 mb-2 space-y-1">
          {topics.length === 0 ? (
            <li className="text-sm text-gray-400 italic">No topics</li>
          ) : topics.map((topic) => {
            const tpct = topic.completion_percent || 0;
            const status = topicStatusLabel(tpct);
            const topicDone = tpct === 100;
            return (
              <li
                key={topic.id}
                className="flex items-center gap-2 text-sm"
              >
                <TopicStatusBadge percent={tpct} />
                <span
                  className={`flex-1 truncate text-left ${
                    topicDone
                      ? "text-darkrock/50 line-through"
                      : "text-gray-700"
                  }`}
                >
                  {topic.title}
                </span>
                <span className={`font-medium ${status.cls}`}>
                  {status.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PlanCard({ plan, colorIndex }) {
  const percent = plan.completion_percent || 0;
  const headerColor = headerColors[colorIndex % headerColors.length];
  const currentLessonIndex = plan.current_position?.lesson_index ?? -1;

  const completedLessons = plan.lessons
    ? plan.lessons.filter((l) => l.completion_percent === 100).length
    : 0;
  const totalLessons = plan.lessons ? plan.lessons.length : 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Card header */}
      <div className={`flex items-center gap-3 p-4 border-b ${headerColor}`}>
        <span className="text-3xl">{plan.emoji || "📚"}</span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white truncate text-xl text-left">
            {plan.subject}
          </h3>
          <p className="text-sm text-white/85 text-left">
            {completedLessons} of {totalLessons} lessons completed
          </p>
        </div>
        <ProgressRing percent={percent} />
      </div>

      {/* Always-visible content */}
      <div className="px-4 pb-4">
        {/* Trend chart */}
        {plan.trend && plan.trend.length > 0 && (
          <div className="mt-3 pb-4">
            <h4 className="text-base font-bold text-primary-700 mb-1 text-left">
              📈 Completion over time
            </h4>
            <CompletionTrend trend={plan.trend} />
          </div>
        )}

        {/* Lesson progress */}
        {plan.lessons && plan.lessons.length > 0 && (
          <div
            className={`pt-4 pb-2 ${plan.trend && plan.trend.length > 0 ? "border-t border-gray-100" : ""}`}
          >
            <h4 className="text-base font-bold text-primary-700 mb-2 text-left">
              📋 Lesson progress
            </h4>
            <div className="space-y-1">
              {plan.lessons.map((lesson, idx) => (
                <LessonRow
                  key={lesson.id || idx}
                  lesson={lesson}
                  index={idx}
                  isCurrent={idx === currentLessonIndex}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StudyPlanProgress({ studyPlans }) {
  if (!studyPlans || studyPlans.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-2">Study Plans</h2>
        <p className="text-gray-400 text-sm">No study plans yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {studyPlans.map((plan, index) => (
        <PlanCard key={plan.id} plan={plan} colorIndex={index} />
      ))}
    </div>
  );
}

export default StudyPlanProgress;
